import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Landmark, Plus, Handshake } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import { listInternalOwners, createInternalOwner, attachChannelPartner } from "../../services/adminOwners";

const emptyOwner = { companyName: "", ownerName: "", emailId: "", mobileNoWa: "", mobileNoCalling: "" };
const emptyCp = { companyName: "", ownerName: "", emailId: "", mobileNoWa: "", mobileNoCalling: "", projectIds: "", propertyConfigIds: "" };

const Field = ({ label, ...props }) => (
  <label className="block text-sm">
    <span className="text-slate-600">{label}</span>
    <input {...props} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
  </label>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      <div className="mt-4 space-y-3">{children}</div>
      <div className="mt-5 flex justify-end gap-2">{onClose}</div>
    </div>
  </div>
);

const idList = (s) =>
  (s || "")
    .split(",")
    .map((x) => parseInt(x.trim(), 10))
    .filter((n) => !Number.isNaN(n));

const InternalOwners = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerForm, setOwnerForm] = useState(null); // create-owner modal state
  const [cpForm, setCpForm] = useState(null); // { owner, data }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listInternalOwners({ pageSize: 200 });
      setRows(res?.items || (Array.isArray(res) ? res : []));
    } catch (e) {
      toast.error("Failed to load internal owners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveOwner = async () => {
    if (!ownerForm.companyName || !ownerForm.emailId) {
      toast.error("Company name and email are required");
      return;
    }
    setSaving(true);
    try {
      await createInternalOwner(ownerForm);
      toast.success("Internal owner created — onboarding email sent");
      setOwnerForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create owner");
    } finally {
      setSaving(false);
    }
  };

  const saveCp = async () => {
    const { owner, data } = cpForm;
    if (!data.companyName || !data.emailId) {
      toast.error("Channel partner company name and email are required");
      return;
    }
    setSaving(true);
    try {
      await attachChannelPartner(owner.customerId, {
        agentData: {
          companyName: data.companyName,
          ownerName: data.ownerName,
          emailId: data.emailId,
          mobileNoWa: data.mobileNoWa,
          mobileNoCalling: data.mobileNoCalling,
        },
        projectIds: idList(data.projectIds),
        propertyConfigIds: idList(data.propertyConfigIds),
      });
      toast.success("Channel partner attached");
      setCpForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach channel partner");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "company", header: "Owner", width: 220, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{r.companyName}</p>
        <p className="text-xs text-slate-500 truncate">{r.ownerName}</p>
      </div>
    ) },
    { key: "email", header: "Email", width: 200, render: (r) => <span className="text-sm text-slate-700">{r.emailId}</span> },
    { key: "mobile", header: "Mobile", width: 150, render: (r) => <span className="text-sm text-slate-700">{r.mobileNoWa || r.mobileNoCalling || "—"}</span> },
    {
      key: "action",
      header: "Channel Partners",
      width: 180,
      align: "center",
      render: (r) => (
        <button
          onClick={() => setCpForm({ owner: r, data: { ...emptyCp } })}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
        >
          <Handshake size={14} /> Attach CP
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Landmark}
        title="Internal Owners"
        subtitle="BuildVizer-managed (developer-less) project owners. Create an owner, then attach channel partners and create projects under it."
        actions={
          <button
            onClick={() => setOwnerForm({ ...emptyOwner })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700"
          >
            <Plus size={16} /> New Owner
          </button>
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-5">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          minWidth={760}
          rowKey={(r, i) => r?.customerId ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <Landmark size={28} className="mx-auto text-slate-300" />
              <p className="text-base font-semibold text-slate-900 mt-2">No internal owners yet</p>
              <p className="text-sm text-slate-500 mt-1">Create a BuildVizer-managed owner to start managing projects internally.</p>
            </div>
          }
        />
      </div>

      {ownerForm ? (
        <Modal
          title="New internal owner"
          onClose={
            <>
              <button onClick={() => setOwnerForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={saveOwner} disabled={saving} className="rounded-lg bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50">
                Create
              </button>
            </>
          }
        >
          <Field label="Company name" value={ownerForm.companyName} onChange={(e) => setOwnerForm({ ...ownerForm, companyName: e.target.value })} />
          <Field label="Owner name" value={ownerForm.ownerName} onChange={(e) => setOwnerForm({ ...ownerForm, ownerName: e.target.value })} />
          <Field label="Email" type="email" value={ownerForm.emailId} onChange={(e) => setOwnerForm({ ...ownerForm, emailId: e.target.value })} />
          <Field label="Mobile (WhatsApp)" value={ownerForm.mobileNoWa} onChange={(e) => setOwnerForm({ ...ownerForm, mobileNoWa: e.target.value })} />
          <Field label="Mobile (calling)" value={ownerForm.mobileNoCalling} onChange={(e) => setOwnerForm({ ...ownerForm, mobileNoCalling: e.target.value })} />
        </Modal>
      ) : null}

      {cpForm ? (
        <Modal
          title={`Attach channel partner — ${cpForm.owner.companyName}`}
          onClose={
            <>
              <button onClick={() => setCpForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={saveCp} disabled={saving} className="rounded-lg bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50">
                Attach
              </button>
            </>
          }
        >
          <Field label="CP company name" value={cpForm.data.companyName} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, companyName: e.target.value } })} />
          <Field label="CP owner name" value={cpForm.data.ownerName} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, ownerName: e.target.value } })} />
          <Field label="CP email" type="email" value={cpForm.data.emailId} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, emailId: e.target.value } })} />
          <Field label="CP mobile (WhatsApp)" value={cpForm.data.mobileNoWa} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, mobileNoWa: e.target.value } })} />
          <Field label="CP mobile (calling)" value={cpForm.data.mobileNoCalling} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, mobileNoCalling: e.target.value } })} />
          <Field label="Project IDs (comma-separated)" value={cpForm.data.projectIds} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, projectIds: e.target.value } })} />
          <Field label="Property config IDs (comma-separated)" value={cpForm.data.propertyConfigIds} onChange={(e) => setCpForm({ ...cpForm, data: { ...cpForm.data, propertyConfigIds: e.target.value } })} />
        </Modal>
      ) : null}
    </div>
  );
};

export default InternalOwners;
